import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-hot-toast";
import { FiDownload, FiBookOpen, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import LearningIdentityCard from "./LearningIdentityCard";

export default function LearningIdentityModal({
  show,
  onHide,
  identity,
  learnerName = "Learner",
  onSeeCourses,
}) {
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  if (!identity) return null;

  const shareText = `I just discovered my Learning Identity on Motivar! 🎓\n\nI'm "${identity.title}" — ${identity.tagline}\n\nDiscover yours 👉 https://motivartech.netlify.app`;

  // ── Handle native share (with WhatsApp fallback) ─────────────────────────
  const handleShare = async () => {
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `I'm "${identity.title}" on Motivar!`,
          text: shareText,
          url: "https://motivartech.netlify.app",
        });
      } else {
        // Fallback to WhatsApp
        const encoded = encodeURIComponent(shareText);
        window.open(`https://api.whatsapp.com/send?text=${encoded}`, "_blank");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        // If native share fails, fall back to WhatsApp
        const encoded = encodeURIComponent(shareText);
        window.open(`https://api.whatsapp.com/send?text=${encoded}`, "_blank");
      }
    } finally {
      setSharing(false);
    }
  };

  // ── Download card as PNG ─────────────────────────────────────────────────
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const cardEl = document.getElementById("learning-identity-card");
      if (!cardEl) {
        toast.error("Card element not found.");
        return;
      }
      const canvas = await html2canvas(cardEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = `${identity.key || "learning-identity"}-persona.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Persona card downloaded!");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Could not download the card. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleSeeCourses = () => {
    onHide();
    if (onSeeCourses) onSeeCourses();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      contentClassName="learning-identity-modal-content"
    >
      {/* Custom close button overlay */}
      <button
        onClick={onHide}
        aria-label="Close"
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 10,
          background: "rgba(0,0,0,0.25)",
          border: "none",
          borderRadius: "50%",
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#fff",
          backdropFilter: "blur(4px)",
        }}
      >
        <FiX size={16} />
      </button>

      <Modal.Body style={{ padding: 0, borderRadius: 20, overflow: "hidden" }}>
        {/* Persona Card */}
        <LearningIdentityCard identity={identity} learnerName={learnerName} />

        {/* Action Buttons */}
        <div
          style={{
            padding: "16px 20px 20px 20px",
            background: "#ffffff",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            gap: 10,
          }}
        >
          {/* Share */}
          <button
            onClick={handleShare}
            disabled={sharing}
            style={{
              flex: 1,
              padding: "12px 10px",
              borderRadius: 12,
              border: "none",
              background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              transition: "opacity 0.2s",
              opacity: sharing ? 0.7 : 1,
            }}
          >
            <FaWhatsapp size={16} />
            {sharing ? "Sharing…" : "Share"}
          </button>

          {/* Download */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              flex: 1,
              padding: "12px 10px",
              borderRadius: 12,
              border: "1.5px solid #e2e8f0",
              background: downloading ? "#f8fafc" : "#fff",
              color: "#334155",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: downloading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              transition: "all 0.2s",
            }}
          >
            <FiDownload size={15} />
            {downloading ? "Saving…" : "Download"}
          </button>

          {/* See Courses */}
          <button
            onClick={handleSeeCourses}
            style={{
              flex: 1,
              padding: "12px 10px",
              borderRadius: 12,
              border: "none",
              background: "linear-gradient(135deg, #00AA87 0%, #00886C 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              boxShadow: "0 4px 14px rgba(0,170,135,0.3)",
              transition: "all 0.2s",
            }}
          >
            <FiBookOpen size={15} />
            See Courses
          </button>
        </div>
      </Modal.Body>

      <style>{`
        .learning-identity-modal-content {
          border: none !important;
          border-radius: 20px !important;
          overflow: hidden;
          box-shadow: 0 25px 60px rgba(15, 23, 42, 0.18) !important;
        }
      `}</style>
    </Modal>
  );
}
